import { useEffect, useState } from 'react';
import { Button, Modal, Space, Spin, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { dermatoDocumentsApi } from '../../api/dermatoDocumentsApi';

const { Text } = Typography;

export interface DocumentPreviewModalProps {
  open: boolean;
  formId: string;
  /** null = nu e selectat niciun document. */
  documentId: string | null;
  fileName?: string | null;
  onClose: () => void;
}

/**
 * Previzualizare inline a unui document PDF (raport Fotofinder, buletin HP…).
 *
 * Fișierul cere Bearer token, deci nu poate fi deschis printr-un URL direct:
 * îl descărcăm ca blob și îl redăm într-un `<iframe>` (viewer-ul PDF al
 * browserului). Butonul „Descarcă" rămâne disponibil, pentru cazul în care
 * operatorul vrea fișierul pe disc.
 */
const DocumentPreviewModal = ({
  open,
  formId,
  documentId,
  fileName,
  onClose,
}: DocumentPreviewModalProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !documentId) {
      setObjectUrl(null);
      setError(null);
      return;
    }

    let cancelled = false;
    let created: string | null = null;

    setLoading(true);
    setError(null);

    dermatoDocumentsApi
      .fetchDocumentBlobUrl(formId, documentId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Documentul nu poate fi afișat. Verifică conexiunea și reîncarcă pagina.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [open, formId, documentId]);

  const handleDownload = async () => {
    if (!documentId) return;
    try {
      await dermatoDocumentsApi.downloadDocument(formId, documentId, fileName ?? undefined);
    } catch {
      message.error('Eroare la descărcarea documentului.');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={fileName ?? 'Previzualizare document'}
      width={900}
      footer={
        <Space>
          <Button onClick={onClose}>Închide</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!documentId}
            onClick={() => void handleDownload()}
          >
            Descarcă
          </Button>
        </Space>
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 0' }} data-testid="preview-loading">
          <Spin />
          <div style={{ marginTop: 12 }}>Se încarcă documentul…</div>
        </div>
      )}
      {!loading && error && (
        <Text type="danger" data-testid="preview-error">
          {error}
        </Text>
      )}
      {!loading && !error && objectUrl && (
        <iframe
          title={fileName ?? 'Previzualizare PDF'}
          src={objectUrl}
          style={{ width: '100%', height: '70vh', border: 'none' }}
          data-testid="preview-frame"
        />
      )}
    </Modal>
  );
};

export default DocumentPreviewModal;
